<template>
  <section class="bg-[radial-gradient(circle_at_top_left,#fff9f1_0%,#f8f1ea_36%,#f3ebe3_100%)] py-5 sm:py-6 lg:py-8">
    <div class="mx-auto max-w-[1480px] px-4 sm:px-6 lg:px-8">
      <div v-if="loading" class="rounded-[28px] border border-dashed border-[#eadfd4] bg-white/90 p-8 text-sm text-muted shadow-sm">
        Loading venue preview...
      </div>

      <div v-else class="space-y-6 lg:space-y-8">
        <nav class="flex flex-wrap items-center gap-2 text-sm text-[#7d6653]">
          <button type="button" class="inline-flex items-center gap-2 rounded-full border border-[#e4d6ca] bg-white px-4 py-2 font-semibold text-ink shadow-sm transition hover:border-brand hover:text-brand" @click="goBack">
            <i class="ri-arrow-left-line"></i>
            Back
          </button>
          <span class="text-[#ccb8a8]">/</span>
          <span class="font-medium">Business</span>
          <span class="text-[#ccb8a8]">/</span>
          <span class="font-medium">{{ businessName }}</span>
          <span class="text-[#ccb8a8]">/</span>
          <span class="font-semibold text-ink">{{ venue.title || 'Venue detail' }}</span>
        </nav>

        <div v-if="error" class="rounded-[22px] border border-amber-200 bg-amber-50 px-5 py-4 text-sm font-medium text-amber-800">
          {{ error }}
        </div>

        <section class="overflow-hidden rounded-[32px] border border-[#eadfd4] bg-white shadow-sm">
          <div class="border-b border-[#efe3d8] px-6 py-5 lg:px-8">
            <div class="flex flex-wrap items-center justify-between gap-4">
              <div>
                <h1 class="mt-2 font-serif text-[2rem] font-semibold leading-tight text-ink lg:text-[2.6rem]">{{ venue.title || 'Venue detail' }}</h1>
                <p class="mt-3 text-[1.05rem] italic text-[#927660]">{{ venue.tagLine || 'Venue preview for the public detail page.' }}</p>
              </div>
              <span class="rounded-full bg-[#fbf1e6] px-4 py-2 text-sm font-semibold text-brand">
                {{ venue.type || 'Venue' }}
              </span>
            </div>
          </div>

          <div class="space-y-6 px-6 py-6 lg:px-8">
            <div class="mx-auto grid max-w-[980px] gap-3 lg:grid-cols-12">
              <button
                type="button"
                class="relative min-h-[300px] overflow-hidden rounded-[28px] bg-[linear-gradient(135deg,#f5e8da_0%,#f9f1e8_54%,#efe1d1_100%)] p-4 text-left sm:min-h-[320px] lg:col-span-6 lg:min-h-[420px] lg:p-5"
                @click="openVenueGalleryById(previewGalleryTiles[0])"
              >
                <img v-if="previewGalleryTiles[0]" :src="previewGalleryTiles[0].src" :alt="venue.title" class="h-full w-full rounded-[22px] object-contain">
                <span v-if="previewGalleryTiles[0]?.isCover" class="absolute left-4 top-4 rounded-full bg-white/95 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-brand shadow-sm">
                  Cover image
                </span>
              </button>

              <div class="grid gap-3 lg:col-span-3 lg:grid-rows-2">
                <button
                  v-for="(image, index) in previewGalleryTiles.slice(1, 3)"
                  :key="`${image.id || 'hero-middle'}-${index}`"
                  type="button"
                  class="relative min-h-[300px] overflow-hidden rounded-[22px] bg-[linear-gradient(135deg,#f5e8da_0%,#f9f1e8_54%,#efe1d1_100%)] p-3 text-left sm:min-h-[300px] lg:min-h-[300px]"
                  @click="openVenueGalleryById(image)"
                >
                  <img :src="image.src" :alt="`${venue.title} ${index + 2}`" class="h-full w-full rounded-[18px] object-contain">
                </button>
              </div>

              <div class="grid gap-3 lg:col-span-3 lg:grid-rows-2">
                <button
                  v-for="(image, index) in previewGalleryTiles.slice(3, 5)"
                  :key="`${image.id || 'hero-right'}-${index}`"
                  type="button"
                  class="relative min-h-[300px] overflow-hidden rounded-[22px] bg-[linear-gradient(135deg,#f5e8da_0%,#f9f1e8_54%,#efe1d1_100%)] p-3 text-left sm:min-h-[300px] lg:min-h-[300px]"
                  @click="openVenueGalleryById(image)"
                >
                  <img :src="image.src" :alt="`${venue.title} ${index + 4}`" class="h-full w-full rounded-[18px] object-contain">
                  <div
                    v-if="image.overlayText"
                    class="absolute inset-0 flex flex-col items-center justify-center bg-[#1f1208]/55 text-center text-white"
                  >
                    <span class="text-[1.45rem] font-semibold leading-none">{{ image.overlayText }}</span>
                    <span class="mt-1.5 text-[10px] font-semibold uppercase tracking-[0.18em]">more photos</span>
                  </div>
                </button>
              </div>
            </div>

            <div class="rounded-[28px] border border-[#eadfd4] bg-[#fffdf9] p-6">
              <div class="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-[#6f5948]">
                <span class="inline-flex items-center gap-2">
                  <i class="ri-map-pin-2-fill text-[#d16c57]"></i>
                  {{ venue.address || 'Location unavailable' }}
                </span>
                <span class="text-[#d8c8bb]">·</span>
                <span>{{ establishedLabel }}</span>
                <span v-if="venue.phone" class="text-[#d8c8bb]">·</span>
                <span v-if="venue.phone" class="inline-flex items-center gap-2">
                  <i class="ri-phone-line text-brand"></i>
                  {{ venue.phoneCode ? `${venue.phoneCode} ` : '' }}{{ venue.phone }}
                </span>
              </div>

              <p class="mt-6 text-[1.05rem] leading-8 text-[#2f241b]">
                {{ venue.about || venueStory }}
              </p>

              <div v-if="venueHighlightsList.length" class="mt-6">
                <p class="text-xs font-semibold uppercase tracking-[0.18em] text-[#b78969]">Highlights</p>
                <div class="mt-3 flex flex-wrap gap-2">
                  <span v-for="item in venueHighlightsList" :key="item" class="rounded-full bg-[#f5eee8] px-3 py-1.5 text-sm font-semibold text-[#6d5542]">
                    {{ item }}
                  </span>
                </div>
              </div>

              <div v-if="inHouseServicesList.length" class="mt-6">
                <p class="text-xs font-semibold uppercase tracking-[0.18em] text-[#b78969]">In-house services</p>
                <div class="mt-4 grid gap-3 md:grid-cols-2">
                  <div v-for="item in inHouseServicesList" :key="item" class="flex items-center gap-3 text-[1rem] text-[#2f241b]">
                    <span class="text-[#2e8a5d]">✓</span>
                    <span>{{ item }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <div class="grid gap-6 xl:grid-cols-[minmax(0,1fr)_360px]">
          <div class="space-y-6">
            <section class="rounded-[28px] border border-[#eadfd4] bg-white p-6 shadow-sm">
              <div>
                <p class="text-xs font-semibold uppercase tracking-[0.18em] text-[#b78969]">Spaces</p>
              <h2 class="mt-2 font-serif text-[1.9rem] font-semibold text-ink">Venue spaces</h2>
            </div>

            <div v-if="spaces.length" class="mt-5 grid gap-4 md:grid-cols-2">
              <article v-for="item in spaces" :key="item.id" class="rounded-[24px] border border-[#eadfd4] bg-[#fffaf5] p-5">
                <div class="flex items-start justify-between gap-4">
                  <div>
                    <h3 class="text-lg font-semibold text-ink">{{ item.name }}</h3>
                    <p class="mt-1 text-sm text-muted">{{ item.spaceType || 'Space' }} · {{ item.setting }}</p>
                  </div>
                  <p class="text-base font-semibold text-[#1f1208]">{{ item.priceLabel }}</p>
                </div>
                <p class="mt-4 text-sm leading-7 text-[#4b3b2f]">{{ item.description || 'No description added.' }}</p>
                <div v-if="item.seatingSummary.length" class="mt-4 flex flex-wrap gap-2">
                  <span v-for="seat in item.seatingSummary" :key="seat" class="rounded-full border border-[#eadfd4] bg-white px-3 py-1 text-xs font-semibold text-[#6f5948]">
                    {{ seat }}
                  </span>
                </div>
              </article>
            </div>
            <p v-else class="mt-5 rounded-[22px] border border-dashed border-[#eadfd4] bg-[#fffaf5] px-5 py-5 text-sm text-muted">
              No spaces configured yet.
            </p>
          </section>

          <section class="rounded-[28px] border border-[#eadfd4] bg-white p-6 shadow-sm">
            <div>
              <p class="text-xs font-semibold uppercase tracking-[0.18em] text-[#b78969]">Packages</p>
              <h2 class="mt-2 font-serif text-[1.9rem] font-semibold text-ink">Venue packages</h2>
            </div>

            <div v-if="packages.length" class="mt-5 grid gap-4 md:grid-cols-2">
              <article v-for="item in packages" :key="item.id" class="rounded-[24px] border border-[#eadfd4] bg-[#fffaf5] p-5">
                <div class="flex items-start justify-between gap-4">
                  <div>
                    <h3 class="text-lg font-semibold text-ink">{{ item.name }}</h3>
                    <div class="mt-2 flex flex-wrap gap-2 text-xs text-[#7d634e]">
                      <span class="rounded-full bg-white px-3 py-1 font-semibold ring-1 ring-[#eadfd4]">{{ item.priceLabel }}</span>
                      <span v-if="item.maxGuests" class="rounded-full bg-white px-3 py-1 font-semibold ring-1 ring-[#eadfd4]">{{ item.maxGuests }} guests</span>
                      <span v-if="item.validHours" class="rounded-full bg-white px-3 py-1 font-semibold ring-1 ring-[#eadfd4]">{{ item.validHours }} hrs</span>
                    </div>
                  </div>
                </div>

                <p class="mt-4 text-sm leading-7 text-[#4b3b2f]">{{ item.description || 'No description added.' }}</p>

                <div v-if="item.includedServices.length" class="mt-4 flex flex-wrap gap-2">
                  <span v-for="service in item.includedServices" :key="service" class="rounded-full border border-[#eadfd4] bg-white px-3 py-1 text-xs font-semibold text-[#6f5948]">
                    {{ service }}
                  </span>
                </div>
              </article>
            </div>
            <p v-else class="mt-5 rounded-[22px] border border-dashed border-[#eadfd4] bg-[#fffaf5] px-5 py-5 text-sm text-muted">
              No packages configured yet.
            </p>
          </section>

          <section class="rounded-[28px] border border-[#eadfd4] bg-white p-6 shadow-sm">
            <div>
              <p class="text-xs font-semibold uppercase tracking-[0.18em] text-[#b78969]">Policies</p>
              <h2 class="mt-2 font-serif text-[1.9rem] font-semibold text-ink">Venue policies</h2>
            </div>

            <div class="mt-6 grid gap-8 md:grid-cols-2">
              <div>
                <p class="text-xs font-semibold uppercase tracking-[0.18em] text-[#b78969]">Venue rules</p>
                <div class="mt-5 space-y-5">
                  <article v-for="item in venueRuleItems" :key="item.label" class="flex items-start gap-4">
                    <span class="mt-0.5 text-[1.35rem]">{{ item.icon }}</span>
                    <div>
                      <p class="text-xs font-semibold uppercase tracking-[0.12em] text-[#b78969]">{{ item.label }}</p>
                      <p class="mt-1 text-[1.02rem] leading-7 text-[#2f241b]">{{ item.value }}</p>
                    </div>
                  </article>
                </div>
              </div>

              <div>
                <p class="text-xs font-semibold uppercase tracking-[0.18em] text-[#b78969]">Booking terms</p>
                <div class="mt-5 space-y-5">
                  <article v-for="item in bookingTermItems" :key="item.label" class="flex items-start gap-4">
                    <span class="mt-0.5 text-[1.35rem]">{{ item.icon }}</span>
                    <div>
                      <p class="text-xs font-semibold uppercase tracking-[0.12em] text-[#b78969]">{{ item.label }}</p>
                      <p class="mt-1 text-[1.02rem] leading-7 text-[#2f241b]">{{ item.value }}</p>
                    </div>
                  </article>
                </div>
              </div>
            </div>
          </section>

        </div>

        <aside class="self-start xl:sticky xl:top-24">
          <section class="rounded-[28px] border border-[#eadfd4] bg-white p-6 shadow-sm">
            <p class="text-xs font-semibold uppercase tracking-[0.18em] text-[#b78969]">Preview</p>
            <h2 class="mt-2 font-serif text-[1.75rem] font-semibold text-ink">Public page summary</h2>
            <p class="mt-2 text-sm leading-7 text-muted">This preview mirrors the public venue detail layout so you can review what guests will see.</p>

            <div class="mt-5 space-y-4">
              <div class="rounded-[22px] border border-[#eadfd4] bg-[#fffaf5] px-4 py-4">
                <p class="text-xs font-semibold uppercase tracking-wide text-[#b78969]">Business</p>
                <p class="mt-2 text-sm font-semibold text-ink">{{ businessName }}</p>
              </div>
              <div class="rounded-[22px] border border-[#eadfd4] bg-[#fffaf5] px-4 py-4">
                <p class="text-xs font-semibold uppercase tracking-wide text-[#b78969]">Venue status</p>
                <p class="mt-2 text-sm font-semibold text-ink">{{ venue.status || 'Draft' }}</p>
              </div>
              <div class="rounded-[22px] border border-[#eadfd4] bg-[#fffaf5] px-4 py-4">
                <p class="text-xs font-semibold uppercase tracking-wide text-[#b78969]">Preview readiness</p>
                <div class="mt-3 space-y-2 text-sm text-[#4b3b2f]">
                  <div class="flex items-center justify-between gap-3">
                    <span>Gallery images</span>
                    <span class="font-semibold text-ink">{{ gallery.length }}</span>
                  </div>
                  <div class="flex items-center justify-between gap-3">
                    <span>Spaces configured</span>
                    <span class="font-semibold text-ink">{{ spaces.length }}</span>
                  </div>
                  <div class="flex items-center justify-between gap-3">
                    <span>Packages configured</span>
                    <span class="font-semibold text-ink">{{ packages.length }}</span>
                  </div>
                  <div class="flex items-center justify-between gap-3">
                    <span>Partners linked</span>
                    <span class="font-semibold text-ink">{{ partners.length }}</span>
                  </div>
                </div>
              </div>
            </div>

            <button
              type="button"
              class="mt-5 w-full rounded-2xl bg-brand px-4 py-3 text-sm font-semibold text-white transition hover:bg-brandDark"
              @click="openVenueGallery()"
            >
              Open full gallery
            </button>
          </section>
        </aside>
      </div>
    </div>
  </div>
</section>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import lightGallery from 'lightgallery'
import lgThumbnail from 'lightgallery/plugins/thumbnail'
import lgZoom from 'lightgallery/plugins/zoom'
import api from '@/services/vendor/api'

const route = useRoute()
const router = useRouter()

const loading = ref(false)
const error = ref('')
const businessName = ref(String(route.query.businessName || 'Business'))
const venue = ref({
  id: '',
  title: '',
  type: '',
  tagLine: '',
  about: '',
  address: '',
  status: '',
  yearEstablished: '',
  phone: '',
  phoneCode: '',
  email: '',
  website: '',
  highlights: [],
  inHouseServices: []
})
const spaces = ref([])
const packages = ref([])
const partners = ref([])
const gallery = ref([])
const galleryLightbox = ref(null)
const policies = ref({
  inHouseCatering: '',
  outsideCatering: '',
  decorPolicy: '',
  musicPolicy: '',
  alcoholInhouse: '',
  alcoholOutside: '',
  minGuests: '',
  maxGuests: '',
  decorTime: '',
  cleanTime: '',
  noiseMusic: '',
  otherHouseRules: '',
  cancelTerms: '',
  depositPercent: ''
})

const previewPlaceholderImages = [
  {
    id: 'placeholder-1',
    src: 'https://images.unsplash.com/photo-1519167758481-83f550bb49b3?auto=format&fit=crop&w=1400&q=80',
    tagName: 'Banquet hall',
    isCover: false,
    isPlaceholder: true
  },
  {
    id: 'placeholder-2',
    src: 'https://images.unsplash.com/photo-1464366400600-7168b8af9bc3?auto=format&fit=crop&w=900&q=80',
    tagName: 'Table setup',
    isCover: false,
    isPlaceholder: true
  },
  {
    id: 'placeholder-3',
    src: 'https://images.unsplash.com/photo-1530103862676-de8c9debad1d?auto=format&fit=crop&w=900&q=80',
    tagName: 'Decor',
    isCover: false,
    isPlaceholder: true
  },
  {
    id: 'placeholder-4',
    src: 'https://images.unsplash.com/photo-1520854221256-17451cc331bf?auto=format&fit=crop&w=900&q=80',
    tagName: 'Outdoor portrait',
    isCover: false,
    isPlaceholder: true
  },
  {
    id: 'placeholder-5',
    src: 'https://images.unsplash.com/photo-1517457373958-b7bdd4587205?auto=format&fit=crop&w=900&q=80',
    tagName: 'Dining room',
    isCover: false,
    isPlaceholder: true
  }
]

const normalizeApiCollection = (data, keys = []) => {
  const root = data?.data ?? data
  if (Array.isArray(root)) return root
  for (const key of keys) {
    if (Array.isArray(root?.[key])) return root[key]
  }
  return []
}

const splitToArray = (value) =>
  String(value || '')
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean)

const normalizeVenue = (item = {}) => ({
  id: String(item?.id || item?.venue_id || item?.vendor_business_venue_id || route.params.venueId || ''),
  title: item?.title || item?.name || 'Untitled venue',
  type: item?.type || item?.venue_type || '',
  tagLine: item?.tag_line || item?.tagLine || '',
  about: item?.about || '',
  address: item?.address || item?.location || '',
  status: String(item?.status || '').trim() === '1' ? 'Active' : item?.status || 'Draft',
  yearEstablished: item?.est_year || item?.year_established || item?.yearEstablished || '',
  phone: item?.phone || '',
  phoneCode: item?.phone_code || item?.phoneCode || '',
  email: item?.email || '',
  website: item?.website || '',
  highlights: splitToArray(item?.highlights || item?.venue_highlights),
  inHouseServices: splitToArray(item?.in_house_services || item?.inHouseServices)
})

const normalizeSpace = (item = {}, index = 0) => {
  const configurations = splitToArray(item?.seat_config || item?.seatConfig)
  const capacities = splitToArray(item?.seat_capacity || item?.seatCapacity)
  const amenities = splitToArray(item?.amenities)
  return {
    id: String(item?.space_id || item?.id || `space-${index}`),
    name: item?.name || 'Untitled space',
    spaceType: item?.space_type || item?.spaceType || '',
    setting: item?.setting || 'Indoor',
    areaSqft: item?.area_sqft || item?.areaSqft || '',
    description: item?.description || '',
    minPrice: item?.min_price || item?.minPrice || '',
    maxPrice: item?.max_price || item?.maxPrice || '',
    priceLabel: formatPriceRange(item?.min_price || item?.minPrice, item?.max_price || item?.maxPrice),
    amenities,
    seatingSummary: configurations.map((label, configIndex) => {
      const capacity = capacities[configIndex]
      return capacity ? `${label}: ${capacity}` : label
    })
  }
}

const normalizePackage = (item = {}, index = 0) => ({
  id: String(item?.package_id || item?.id || `package-${index}`),
  name: item?.name || 'Untitled package',
  description: item?.description || '',
  price: item?.price || '',
  maxGuests: item?.max_guests || item?.maxGuests || '',
  validHours: item?.valid_hours || item?.validHours || '',
  includedServices: splitToArray(item?.included_services || item?.includedServices),
  priceLabel: formatCurrency(item?.price) || 'Price on request'
})

const normalizePartner = (item = {}, index = 0) => ({
  id: String(item?.vendor_partner_id || item?.id || `partner-${index}`),
  name: item?.name || 'Partner',
  category: item?.category || '',
  phone: item?.phone || ''
})

const normalizeVenueGalleryEntries = (source) => {
  const items = Array.isArray(source) ? source : [source]
  return items
    .flatMap((item, itemIndex) => {
      if (!item) return []
      if (typeof item === 'string') {
        return [{
          id: `gallery-${itemIndex}-${item}`,
          src: item,
          tagName: '',
          isCover: false
        }]
      }
      const imageKeys = ['image1', 'image2', 'image3', 'image4', 'image', 'image_url', 'url', 'path']
      const entryId = String(item?.id || item?.gallery_id || item?.venue_gallery_id || item?.img_id || '')
      return imageKeys
        .map((key, keyIndex) => {
          const src = item?.[key] || ''
          if (!src) return null
          return {
            id: entryId || `gallery-${itemIndex}-${keyIndex}-${src}`,
            src,
            tagName: String(item?.tag_name || item?.tagName || item?.name || '').trim(),
            isCover: String(item?.is_cover || item?.isCover || '0') === '1'
          }
        })
        .filter(Boolean)
    })
    .filter((entry) => entry?.src)
}

const formatCurrency = (value) => {
  const numeric = Number(String(value || '').replace(/[^\d.]/g, ''))
  return Number.isFinite(numeric) && numeric > 0 ? `Rs. ${numeric.toLocaleString('en-IN')}` : ''
}

const formatPriceRange = (minPrice, maxPrice) => {
  const minValue = formatCurrency(minPrice)
  const maxValue = formatCurrency(maxPrice)
  if (minValue && maxValue) return `${minValue} - ${maxValue}`
  return minValue || maxValue || 'Price on request'
}

const venueCity = computed(() => {
  const address = String(venue.value.address || '').trim()
  if (!address) return 'City'
  const segments = address.split(',').map((item) => item.trim()).filter(Boolean)
  return segments.length >= 2 ? segments[segments.length - 2] : segments[0] || 'City'
})

const establishedLabel = computed(() => venue.value.yearEstablished ? `Est. ${venue.value.yearEstablished}` : 'Est. Not set')

const previewGalleryTiles = computed(() => {
  const sortedGallery = [...gallery.value].sort((left, right) => Number(right.isCover) - Number(left.isCover))
  const baseTiles = sortedGallery.slice(0, 5)
  const missing = Math.max(0, 5 - baseTiles.length)
  const filledTiles = [...baseTiles, ...previewPlaceholderImages.slice(0, missing)].map((item, index) => ({
    ...item,
    id: item.id || `preview-tile-${index}`,
    isPlaceholder: Boolean(item.isPlaceholder),
    overlayText: ''
  }))

  if (!filledTiles.length) {
    return previewPlaceholderImages.map((item, index) => ({
      ...item,
      id: item.id || `preview-tile-${index}`,
      overlayText: ''
    }))
  }

  const hiddenCount = Math.max(0, sortedGallery.length - 5)
  if (filledTiles[4]) {
    filledTiles[4] = {
      ...filledTiles[4],
      overlayText: hiddenCount ? `+${hiddenCount}` : ''
    }
  }

  return filledTiles
})

const lightboxGalleryImages = computed(() => {
  const source = gallery.value.length
    ? [...gallery.value].sort((left, right) => Number(right.isCover) - Number(left.isCover))
    : previewPlaceholderImages

  return source.map((item, index) => ({
    id: item.id || `lightbox-${index}`,
    src: item.src,
    thumb: item.src,
    tagName: item.tagName || '',
    isCover: Boolean(item.isCover),
    isPlaceholder: Boolean(item.isPlaceholder)
  }))
})

const venueStory = computed(() => (
  venue.value.about
    || `Established to host memorable celebrations in ${venueCity.value}, ${venue.value.title || 'this venue'} brings together flexible spaces, hospitality support, and a layout designed for weddings, parties, and social events.`
))

const venueHighlightsList = computed(() => {
  const items = venue.value.highlights.length
    ? venue.value.highlights
    : ['Award Winning', '5-Star Rated', 'Valet Parking', 'Wheelchair Accessible', 'Power Backup', 'CCTV Surveillance']
  return items.slice(0, 8)
})

const inHouseServicesList = computed(() => {
  const packageServices = packages.value.flatMap((item) => item.includedServices || [])
  const venueServices = Array.isArray(venue.value.inHouseServices) ? venue.value.inHouseServices : []
  const servicePool = [...venueServices, ...packageServices]
    .map((item) => String(item || '').trim())
    .filter(Boolean)

  const uniqueServices = [...new Set(servicePool)]
  return uniqueServices.length
    ? uniqueServices.slice(0, 8)
    : ['Catering', 'DJ / Music', 'Valet Parking', 'Event Coordination', 'Decoration', 'Photography', 'Bar', 'Guest Support']
})

const venueRuleItems = computed(() => ([
  {
    icon: '🍽️',
    label: 'Catering',
    value: [policies.value.inHouseCatering, policies.value.outsideCatering].filter(Boolean).join('. ') || 'Catering policy not added yet.'
  },
  {
    icon: '🌸',
    label: 'Decoration',
    value: policies.value.decorPolicy || 'Decoration policy not added yet.'
  },
  {
    icon: '🎵',
    label: 'DJ / Music',
    value: policies.value.musicPolicy || 'Music policy not added yet.'
  },
  {
    icon: '🍸',
    label: 'Alcohol',
    value: [policies.value.alcoholInhouse, policies.value.alcoholOutside].filter(Boolean).join('. ') || 'Alcohol policy not added yet.'
  },
  {
    icon: '🔊',
    label: 'Noise curfew',
    value: policies.value.noiseMusic ? `Music must end by ${policies.value.noiseMusic}.` : 'Noise or music curfew not added yet.'
  },
  {
    icon: '📋',
    label: 'House rules',
    value: policies.value.otherHouseRules || 'No additional house rules added yet.'
  }
]))

const bookingTermItems = computed(() => ([
  {
    icon: '👥',
    label: 'Guest count',
    value: policies.value.minGuests || policies.value.maxGuests
      ? `${policies.value.minGuests || 'Flexible'}-${policies.value.maxGuests || 'Flexible'} guests`
      : 'Guest count policy not added yet.'
  },
  {
    icon: '🔧',
    label: 'Setup time',
    value: policies.value.decorTime || 'Setup timing not added yet.'
  },
  {
    icon: '🧹',
    label: 'Cleanup time',
    value: policies.value.cleanTime || 'Cleanup timing not added yet.'
  },
  {
    icon: '💳',
    label: 'Deposit',
    value: policies.value.depositPercent ? `${policies.value.depositPercent}% of total to confirm booking` : 'Deposit terms not added yet.'
  },
  {
    icon: '↩️',
    label: 'Cancellation',
    value: policies.value.cancelTerms || 'Cancellation policy not added yet.'
  }
]))

const loadVenueGalleryPreview = async (venueId) => {
  const categoriesResponse = await api.getVenueGalleryCategories(new FormData())
  const categories = normalizeApiCollection(categoriesResponse?.data, ['categories', 'gallery_categories', 'items', 'list'])
  const tagIds = categories
    .map((item) => String(item?.id || item?.gallery_category_id || item?.tag_id || '').trim())
    .filter(Boolean)

  if (!tagIds.length) {
    const formData = new FormData()
    formData.append('venue_id', venueId)
    const galleryResponse = await api.getVenueGallery(formData)
    return normalizeVenueGalleryEntries(normalizeApiCollection(galleryResponse?.data, ['gallery', 'images', 'items', 'list']))
  }

  const responses = await Promise.all(tagIds.map(async (tagId) => {
    const formData = new FormData()
    formData.append('venue_id', venueId)
    formData.append('tag_id', tagId)
    const galleryResponse = await api.getVenueGallery(formData)
    return normalizeVenueGalleryEntries(normalizeApiCollection(galleryResponse?.data, ['gallery', 'images', 'items', 'list']))
  }))

  const seen = new Set()
  return responses.flat().filter((item) => {
    const key = `${item.id}-${item.src}`
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })
}

const loadVenue = async () => {
  loading.value = true
  error.value = ''

  try {
    const businessFormData = new FormData()
    businessFormData.append('business_id', route.params.id)
    const venueFormData = new FormData()
    venueFormData.append('business_id', route.params.id)
    venueFormData.append('venue_id', route.params.venueId)
    const [businessResult, venueResult] = await Promise.allSettled([
      api.editBusinessVendor(businessFormData),
      api.listVenuesBusinessVendor(venueFormData)
    ])

    let hasPartialFailure = false

    if (businessResult.status === 'fulfilled') {
      const businessPayload = Array.isArray(businessResult.value?.data?.data)
        ? businessResult.value.data.data[0]
        : businessResult.value?.data?.data || businessResult.value?.data || {}
      businessName.value = businessPayload?.business_name || businessPayload?.businessName || businessPayload?.name || businessName.value
    } else {
      hasPartialFailure = true
    }

    if (venueResult.status === 'fulfilled') {
      const venuePayload = venueResult.value?.data?.data || venueResult.value?.data || {}
      const venueItems = Array.isArray(venuePayload)
        ? venuePayload
        : venuePayload?.venues || venuePayload?.items || venuePayload?.list || venuePayload?.venue_list || []
      const matchedVenue = Array.isArray(venueItems)
        ? venueItems.find((item) => String(item?.id || item?.venue_id || item?.vendor_business_venue_id || '') === String(route.params.venueId))
        : null
      venue.value = normalizeVenue(matchedVenue || venueItems[0] || {})
    } else {
      hasPartialFailure = true
      venue.value = normalizeVenue({
        id: route.params.venueId,
        title: route.query.venueTitle || 'Venue detail',
        address: route.query.venueAddress || '',
        status: 'Draft'
      })
    }

    const spacesFormData = new FormData()
    spacesFormData.append('venue_id', route.params.venueId)
    const packagesFormData = new FormData()
    packagesFormData.append('venue_id', route.params.venueId)
    const partnersFormData = new FormData()
    partnersFormData.append('venue_id', route.params.venueId)
    const policiesFormData = new FormData()
    policiesFormData.append('venue_id', route.params.venueId)

    const [spacesResult, packagesResult, partnersResult, policiesResult, galleryResult] = await Promise.allSettled([
      api.listVenueSpaces(spacesFormData),
      api.getPackagesVenue(packagesFormData),
      api.listVenueVendorPartners(partnersFormData),
      api.editPoliciesVenue(policiesFormData),
      loadVenueGalleryPreview(String(route.params.venueId))
    ])

    if (spacesResult.status === 'fulfilled') {
      const spaceData = spacesResult.value?.data?.data || spacesResult.value?.data || {}
      const items = Array.isArray(spaceData) ? spaceData : spaceData?.spaces || spaceData?.items || spaceData?.list || []
      spaces.value = items.map((item, index) => normalizeSpace(item, index))
    } else {
      spaces.value = []
    }

    if (packagesResult.status === 'fulfilled') {
      const packageData = packagesResult.value?.data?.data || packagesResult.value?.data || {}
      const items = Array.isArray(packageData) ? packageData : packageData?.packages || packageData?.items || packageData?.list || []
      packages.value = items.map((item, index) => normalizePackage(item, index))
    } else {
      packages.value = []
    }

    if (partnersResult.status === 'fulfilled') {
      const partnerData = partnersResult.value?.data?.data || partnersResult.value?.data || {}
      const items = Array.isArray(partnerData) ? partnerData : partnerData?.partners || partnerData?.vendor_partners || partnerData?.items || partnerData?.list || []
      partners.value = items.map((item, index) => normalizePartner(item, index))
    } else {
      partners.value = []
      hasPartialFailure = true
    }

    if (policiesResult.status === 'fulfilled') {
      const policyItem = normalizeApiCollection(policiesResult.value?.data, ['policies', 'policy', 'items', 'list'])[0] || policiesResult.value?.data?.data || policiesResult.value?.data || {}
      policies.value = {
        inHouseCatering: policyItem?.in_house_catering || '',
        outsideCatering: policyItem?.outside_catering || '',
        decorPolicy: policyItem?.decor_policy || '',
        musicPolicy: policyItem?.music_policy || '',
        alcoholInhouse: policyItem?.alcohol_inhouse || '',
        alcoholOutside: policyItem?.alcohol_outside || '',
        minGuests: policyItem?.min_guests || '',
        maxGuests: policyItem?.max_guests || '',
        decorTime: policyItem?.decor_time || '',
        cleanTime: policyItem?.clean_time || '',
        noiseMusic: policyItem?.noise_music || '',
        otherHouseRules: policyItem?.other_house_rules || '',
        cancelTerms: policyItem?.cancel_terms || '',
        depositPercent: policyItem?.deposit_percent || ''
      }
    } else {
      hasPartialFailure = true
    }

    if (galleryResult.status === 'fulfilled') {
      gallery.value = galleryResult.value
    } else {
      gallery.value = []
      hasPartialFailure = true
    }

    if (hasPartialFailure) {
      error.value = 'Some venue details could not be loaded. Showing the preview with fallback content.'
    }
  } catch (err) {
    error.value = 'Some venue details could not be loaded. Showing the preview with fallback content.'
  } finally {
    loading.value = false
  }
}

const destroyGalleryLightbox = () => {
  if (galleryLightbox.value) {
    galleryLightbox.value.destroy(true)
    galleryLightbox.value = null
  }
}

const openVenueGallery = (startIndex = 0) => {
  if (!lightboxGalleryImages.value.length) return
  destroyGalleryLightbox()
  galleryLightbox.value = lightGallery(document.createElement('div'), {
    dynamic: true,
    dynamicEl: lightboxGalleryImages.value.map((image) => ({
      src: image.src,
      thumb: image.thumb,
      subHtml: image.tagName ? `<span class="text-sm font-semibold">${image.tagName}</span>` : ''
    })),
    index: Math.max(0, startIndex),
    plugins: [lgThumbnail, lgZoom],
    download: false,
    actualSize: false,
    mobileSettings: {
      controls: true,
      showCloseIcon: true,
      download: false
    }
  })
  galleryLightbox.value.openGallery(startIndex)
}

const openVenueGalleryById = (image) => {
  const imageId = String(image?.id || '')
  const startIndex = lightboxGalleryImages.value.findIndex((item) => String(item.id || '') === imageId)
  openVenueGallery(startIndex >= 0 ? startIndex : 0)
}

const goBack = () => {
  router.push({
    name: 'business-detail',
    params: { id: route.params.id }
  })
}

onMounted(() => {
  loadVenue()
})

onBeforeUnmount(() => {
  destroyGalleryLightbox()
})
</script>
